import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Heading,
  Text,
  Card,
  Flex,
  Badge,
  Button,
  IconButton,
  Tooltip,
} from '@radix-ui/themes';
import {
  MixerHorizontalIcon,
  PlusIcon,
  Pencil1Icon,
  TrashIcon,
} from '@radix-ui/react-icons';
import { GetSignalsDocument } from '../../graphql/queries/GetSignals.generated';
import { useDeleteSignalMutation } from '../../graphql/mutations/DeleteSignal.generated';
import TimeAgoTooltip from '../../components/TimeAgoTooltip';
import DeleteConfirmDialog from '../../components/DeleteConfirmDialog';
import GraphQLTable, { Column } from '../../components/GraphQLTable/GraphQLTable';
import type { GetSignalsQuery } from '../../graphql/queries/GetSignals.generated';

const SignalTypeBadge = ({ signalType }: { signalType: string }) => {
  const config: Record<
    string,
    { color: 'blue' | 'purple' | 'orange' | 'green'; label: string }
  > = {
    TRANSACTION: { color: 'blue', label: 'Transaction' },
    LOG: { color: 'purple', label: 'Log' },
    BLOCK: { color: 'orange', label: 'Block' },
    NETWORK: { color: 'green', label: 'Network' },
  };

  const { color, label } = config[signalType] || {
    color: 'blue',
    label: signalType,
  };

  return (
    <Badge color={color} variant="soft" size="1">
      {label}
    </Badge>
  );
};

type SignalNode = NonNullable<
  GetSignalsQuery['signals']['edges'][number]['node']
>;

const SignalList: React.FC = () => {
  const navigate = useNavigate();
  const [deleteSignal, { loading: deleteLoading }] = useDeleteSignalMutation();
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [signalToDelete, setSignalToDelete] = useState<{
    id: string;
    name: string;
  } | null>(null);

  const handleDeleteClick = (
    e: React.MouseEvent,
    signal: { id: string; name: string },
  ) => {
    e.stopPropagation();
    setSignalToDelete(signal);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!signalToDelete) return;
    await deleteSignal({ variables: { id: signalToDelete.id } });
    setDeleteDialogOpen(false);
    setSignalToDelete(null);
  };

  const handleEditClick = (e: React.MouseEvent, signalId: string) => {
    e.stopPropagation();
    navigate(`/signals/${signalId}`);
  };

  const columns: Column<SignalNode>[] = [
    {
      header: 'Name',
      accessor: 'name',
      render: ({ row }) => (
        <Flex direction="column">
          <Text size="2" weight="medium">
            {row.name}
          </Text>
          {row.description && (
            <Text size="1" color="gray">
              {row.description}
            </Text>
          )}
        </Flex>
      ),
    },
    {
      header: 'Type',
      accessor: 'signalType',
      render: ({ value }) => <SignalTypeBadge signalType={value as string} />,
    },
    {
      header: 'Fingerprints',
      render: ({ row }) => (
        <Text size="2" color="gray">
          {row.fingerprints.length}
        </Text>
      ),
    },
    {
      header: 'Triggers',
      render: () => (
        <Text size="2" color="gray">
          0
        </Text>
      ),
    },
    {
      header: 'Created',
      accessor: 'createdAt',
      render: ({ value }) => <TimeAgoTooltip value={value as string} />,
    },
    {
      header: null,
      render: ({ row }) => (
        <Flex gap="1" justify="end" align="center">
          <Tooltip content="Edit">
            <IconButton
              size="1"
              variant="ghost"
              color="gray"
              onClick={(e) => handleEditClick(e, row.id)}
            >
              <Pencil1Icon width={14} height={14} />
            </IconButton>
          </Tooltip>
          <Tooltip content="Delete">
            <IconButton
              size="1"
              variant="ghost"
              color="red"
              onClick={(e) => handleDeleteClick(e, row)}
            >
              <TrashIcon width={14} height={14} />
            </IconButton>
          </Tooltip>
        </Flex>
      ),
      style: { width: '80px', textAlign: 'right' },
    },
  ];

  return (
    <Box>
      <Box pb="4" px="1">
        <Flex align="center" justify="between">
          <Flex align="center" gap="3">
            <Box
              style={{
                padding: '8px',
                borderRadius: '8px',
                backgroundColor: 'var(--purple-3)',
                color: 'var(--purple-11)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <MixerHorizontalIcon width={24} height={24} />
            </Box>
            <Box>
              <Heading as="h1" size="5" weight="bold">
                Signals
              </Heading>
              <Text size="2" color="gray">
                Define patterns for monitoring blockchain activity
              </Text>
            </Box>
          </Flex>
          <Button onClick={() => navigate('/signals/new')}>
            <PlusIcon />
            Create Signal
          </Button>
        </Flex>
      </Box>

      <Card size="2">
        <Box
          style={{
            marginLeft: 'calc(-1 * var(--space-4))',
            marginRight: 'calc(-1 * var(--space-4))',
            marginBottom: 'calc(-1 * var(--space-4))',
          }}
        >
          <GraphQLTable
            query={GetSignalsDocument}
            columns={columns}
            connectionPath="signals"
            size="2"
            removeBottomBorder
            noDataMessage="No signals yet. Create signals to define patterns for monitoring blockchain activity."
            onRowClick={(row) => navigate(`/signals/${row.id}`)}
          />
        </Box>
      </Card>

      <DeleteConfirmDialog
        open={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
        title="Delete Signal"
        description={`Are you sure you want to delete "${signalToDelete?.name}"? This action cannot be undone.`}
        onConfirm={handleDeleteConfirm}
        loading={deleteLoading}
      />
    </Box>
  );
};

export default SignalList;
